import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  useTheme,
  Link,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BRAND_TABLE_TEXT_COLOR } from "../styles/tableColors";

interface BorrowerProfile {
  name: string;
  email: string;
  phone?: string;
  type?: string;
  address?: string;
  mobilePhone?: string;
  primaryContact?: string;
  contactId?: string;
}


const ProfileSettings: React.FC = () => {
  const theme = useTheme();
  const [profile, setProfile] = useState<BorrowerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`/api/borrower-profile`, { withCredentials: true })
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  // helper once in a utils file
const formatAddress = (addr?: Record<string, unknown>): string => {
  if (!addr || typeof addr !== 'object') return '';
  // support standard and UI API shapes
  const street = addr.street || addr.Street || addr.billingStreet || addr.BillingStreet;
  const city = addr.city || addr.City || addr.billingCity || addr.BillingCity;
  // Prefer StateCode/CountryCode when available (SF picklists)
  const state = addr.stateCode || addr.StateCode || addr.state || addr.State || addr.billingState || addr.BillingState;
  const postal = addr.postalCode || addr.PostalCode || addr.billingPostalCode || addr.BillingPostalCode;
  const country = addr.countryCode || addr.CountryCode || addr.country || addr.Country || addr.billingCountry || addr.BillingCountry;

  return [street, city, state, postal, country]
    .map(v => (v ?? '').toString().trim())
    .filter(v => v.length > 0)
    .join(', ');
};


  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  if (!profile)
    return <Typography color="error">Unable to load profile.</Typography>;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "transparent",
        p: 3,
        position: "relative",
      }}
    >
      <Box sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ color: "#fff", mb: 2, ml: 0, p: 1, display: "block" }}
          aria-label="Go back"
        >
          <ArrowBackIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: theme.palette.mode === "dark" ? "#444" : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 80, color: "#98773b" }} />
          </Box>
          <Box sx={{ ml: 2 }}>
            <Typography fontWeight={600} color="#fff">
              {profile.primaryContact?.trim() || profile.name || ''}
            </Typography>
            <Typography color="#fff">{profile.email}</Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 2, color: theme.palette.divider }} />
        <Card
          sx={{
            bgcolor: theme.palette.mode === "dark" ? "#18181b" : "#fff",
            color: theme.palette.text.primary,
            borderRadius: 3,
            p: 3,
          }}
        >
          <Field label="Borrower Name" value={profile.name || ''} />
          <Field label="Name" value={profile.primaryContact || ''} />
          <Field label="Email" value={profile.email || ''} />
          <Field label="Mobile Phone" value={profile.mobilePhone || ''} />
          <Field 
            label="Address" 
            value={
              profile.address 
                ? (typeof profile.address === 'string' 
                    ? profile.address 
                    : formatAddress(profile.address))
                : ''} 
          />
        </Card>
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <InfoOutlinedIcon
            sx={{ color: '#fff', fontSize: 18, mr: 1, verticalAlign: "middle" }}
          />
          <Typography variant="caption" color="#fff" sx={{ lineHeight: 1.2 }}>
            If you wish to change any details, please contact{' '}
            <Link 
              href="https://momentafinance.co.uk/contact-us/" 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main }}
            >
              Momenta Finance
            </Link>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color={theme.palette.text.secondary}>
        {label}
      </Typography>
      <Typography fontWeight={600} color={BRAND_TABLE_TEXT_COLOR}>
        {value}
      </Typography>
      <Divider sx={{ my: 2, color: theme.palette.divider }} />
    </Box>
  );
};

export default ProfileSettings;
